import { Building2, Plus } from "lucide-react";
import { useState } from "react";
import { Button, Empty, ErrorNote, Modal, Spinner, toast } from "../components/ui";
import { api, APP_NAMES, useApi, type AppId } from "../lib/api";

interface TenantRow {
  tenantId: string;
  name: string;
  signInMode: "cognito" | "federated";
  apps: AppId[];
  status: "active" | "suspended";
  emailDomains: string[];
  idpReady?: boolean;
  createdAt: string;
}

/** Platform console: create tenants and license apps (design §4.3). */
export default function Tenants() {
  const { data, error, loading, reload } = useApi<{ tenants: TenantRow[] }>("/platform/tenants");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TenantRow | null>(null);
  const base = location.hostname.replace(/^admin\./, "");

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink">Tenants</h1>
          <p className="text-sm text-muted">Each tenant signs in at its own address under {base}.</p>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Plus className="size-4" aria-hidden /> New tenant
        </Button>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : data?.tenants.length === 0 ? (
        <Empty icon={<Building2 className="size-8" />} title="No tenants yet">
          Create the first one, for example INTELLIAS_DEM.
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line bg-surface">
          <table className="w-full text-sm">
            <thead className="text-left text-muted border-b border-line">
              <tr>
                <th className="px-4 py-2 font-medium">Tenant</th>
                <th className="px-4 py-2 font-medium">Sign-in</th>
                <th className="px-4 py-2 font-medium">Apps</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {data?.tenants.map((t) => (
                <tr key={t.tenantId} className="border-b border-line last:border-0">
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink">{t.name}</div>
                    <a className="text-xs text-accent-text hover:underline" href={`https://${t.tenantId}.${base}`} target="_blank" rel="noreferrer">
                      {t.tenantId}.{base}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-ink-2">{t.signInMode === "federated" ? `Federated${t.idpReady ? "" : " (IdP pending)"}` : "Portal accounts"}</td>
                  <td className="px-4 py-3 text-ink-2">{t.apps.map((a) => APP_NAMES[a]).join(", ") || "None"}</td>
                  <td className="px-4 py-3">
                    <span className={t.status === "active" ? "text-good" : "text-warn"}>{t.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" onClick={() => setEditing(t)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <TenantForm onClose={() => setCreating(false)} onSaved={() => (setCreating(false), void reload())} />}
      {editing && <TenantForm tenant={editing} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), void reload())} />}
    </div>
  );
}

function TenantForm({ tenant, onClose, onSaved }: { tenant?: TenantRow; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    tenantId: tenant?.tenantId ?? "",
    name: tenant?.name ?? "",
    signInMode: tenant?.signInMode ?? "cognito",
    emailDomains: tenant?.emailDomains.join(", ") ?? "",
    apps: tenant?.apps ?? (["ja"] as AppId[]),
    status: tenant?.status ?? "active",
    adminEmail: "",
    adminName: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: unknown) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const emailDomains = f.emailDomains.split(/[\s,]+/).filter(Boolean);
      if (tenant) await api(`/platform/tenants/${tenant.tenantId}`, { method: "PUT", body: { name: f.name, emailDomains, apps: f.apps, status: f.status } });
      else await api("/platform/tenants", { body: { ...f, emailDomains, adminEmail: f.adminEmail || undefined, adminName: f.adminName || undefined } });
      toast(tenant ? "Tenant saved" : "Tenant created");
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
  return (
    <Modal
      title={tenant ? `Edit ${tenant.name}` : "New tenant"}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            {tenant ? "Save" : "Create tenant"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        {!tenant && (
          <label className="block text-sm">
            <span className="text-ink-2 font-medium">Tenant id</span>
            <input className={input} value={f.tenantId} onChange={(e) => set("tenantId", e.target.value.toLowerCase())} placeholder="fresno-usd" />
            <span className="text-xs text-muted">Becomes the address: {f.tenantId || "tenant"}.{location.hostname.replace(/^admin\./, "")}. It can't be changed later.</span>
          </label>
        )}
        <label className="block text-sm">
          <span className="text-ink-2 font-medium">Name</span>
          <input className={input} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Fresno Unified School District" />
        </label>
        {!tenant && (
          <fieldset className="text-sm">
            <legend className="text-ink-2 font-medium mb-1">Sign-in</legend>
            {(["cognito", "federated"] as const).map((m) => (
              <label key={m} className="flex items-center gap-2 py-0.5">
                <input type="radio" name="mode" checked={f.signInMode === m} onChange={() => set("signInMode", m)} />
                {m === "cognito" ? "Portal accounts (email invitation, authenticator app required)" : "Their own Entra / SSO (set up after creation)"}
              </label>
            ))}
          </fieldset>
        )}
        <label className="block text-sm">
          <span className="text-ink-2 font-medium">Email domains</span>
          <input className={input} value={f.emailDomains} onChange={(e) => set("emailDomains", e.target.value)} placeholder="fresnounified.org" />
          <span className="text-xs text-muted">Users must have an email at one of these. Leave empty to allow any.</span>
        </label>
        <fieldset className="text-sm">
          <legend className="text-ink-2 font-medium mb-1">Licensed apps</legend>
          {(Object.keys(APP_NAMES) as AppId[]).map((a) => (
            <label key={a} className="flex items-center gap-2 py-0.5">
              <input type="checkbox" checked={f.apps.includes(a)} onChange={(e) => set("apps", e.target.checked ? [...f.apps, a] : f.apps.filter((x) => x !== a))} />
              {APP_NAMES[a]}
            </label>
          ))}
        </fieldset>
        {tenant ? (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.status === "suspended"} onChange={(e) => set("status", e.target.checked ? "suspended" : "active")} />
            Suspended (nobody can sign in)
          </label>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-ink-2 font-medium">First admin email</span>
              <input className={input} value={f.adminEmail} onChange={(e) => set("adminEmail", e.target.value)} />
            </label>
            <label className="block text-sm">
              <span className="text-ink-2 font-medium">Name</span>
              <input className={input} value={f.adminName} onChange={(e) => set("adminName", e.target.value)} />
            </label>
          </div>
        )}
      </div>
    </Modal>
  );
}
